'use client';
import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import BackToTopButton from '../components/BackToTopButton';
import AshaIcon from '@/components/ui/AshaIcon';
import { CheckCircle, ArrowRight, Award, Users, Heart, TrendingUp } from 'lucide-react';

const pillars = [
{
  id: 'pillar-evidence',
  icon: 'success-recovery' as const,
  title: 'Evidence-Based Care',
  subtitle: 'Science-First Treatment Decisions',
  description:
  'Every treatment protocol at ASHA is grounded in peer-reviewed clinical research. Our therapists undergo continuous education to integrate the latest evidence into daily practice — ensuring you receive care that works, not care that\'s simply familiar.',
  stat: '94%',
  statLabel: 'Patient Satisfaction Score'
},
{
  id: 'pillar-personalized',
  icon: 'physical-therapy' as const,
  title: 'Personalized Treatment Plans',
  subtitle: 'No Two Recoveries Are Identical',
  description:
  'Initial evaluation takes 60–90 minutes. We assess your full movement profile, medical history, lifestyle demands, and personal goals — then build a plan that\'s genuinely yours. Progress is tracked weekly and plans are adjusted in real time.',
  stat: '60–90 min',
  statLabel: 'Initial Evaluation Time'
},
{
  id: 'pillar-functional',
  icon: 'sports-rehab' as const,
  title: 'Functional Real-World Recovery',
  subtitle: 'Beyond Pain Relief — Back to Life',
  description:
  'Our definition of recovery isn\'t simply the absence of pain — it\'s the ability to perform the activities that matter most to you. Whether that\'s returning to competitive sport, lifting your grandchild, or walking without a limp, we measure success by your life outcomes.',
  stat: '87%',
  statLabel: 'Return-to-Activity Rate'
}];


const milestones = [
{ year: '2008', title: 'Founded in Chicago', description: 'ASHA Recovery Group opened its first clinic in the Chicago Loop with a team of 4 licensed therapists.' },
{ year: '2012', title: 'JCAHO Accreditation', description: 'Achieved Joint Commission accreditation — one of the first outpatient PT networks in Illinois to do so.' },
{ year: '2016', title: 'Expanded to 8 Clinics', description: 'Rapid growth across Chicagoland driven by physician referral partnerships and patient outcomes.' },
{ year: '2020', title: 'Telehealth Launch', description: 'Launched telehealth PT services during the pandemic, serving over 400 patients remotely in the first year.' },
{ year: '2024', title: '18 Locations & Growing', description: 'Now operating 18 clinics across the metro area with over 120 licensed therapists on staff.' }];


const teamMembers = [
{
  id: 'team-grethel',
  name: 'Grethel Lopez',
  title: 'CEO & Practice Lead Technician',
  bio: 'With over 15 years in rehabilitation medicine, Grethel founded ASHA with a singular vision: evidence-based care delivered with genuine compassion. She holds a DPT from Northwestern University and is a certified orthopedic clinical specialist.',
  photo: '/assets/images/WhatsApp_Image_2026-03-11_at_9.52.29_PM-1777630669856.jpeg'
},
{
  id: 'team-dr-reyes',
  name: 'Dr. Marcus Reyes, PT, DPT',
  title: 'Director of Clinical Operations',
  bio: 'Dr. Reyes oversees clinical quality across all 18 locations. A former Division I athletic trainer, he specializes in sports rehabilitation and return-to-sport protocols.',
  photo: null,
  initials: 'MR'
},
{
  id: 'team-dr-kim',
  name: 'Dr. Sarah Kim, OTR/L',
  title: 'Director of Occupational Therapy',
  bio: 'Dr. Kim leads ASHA\'s occupational therapy division, with specialized expertise in neurological rehabilitation and upper extremity conditions. She trained at Rush University Medical Center.',
  photo: null,
  initials: 'SK'
},
{
  id: 'team-dr-osei',
  name: 'Dr. Kwame Osei, PT, SCS',
  title: 'Sports Rehabilitation Lead',
  bio: 'A board-certified sports clinical specialist, Dr. Osei has worked with professional and collegiate athletes across the Midwest. He brings elite-level rehabilitation protocols to every patient.',
  photo: null,
  initials: 'KO'
}];


const stats = [
{ icon: <Users size={24} />, value: '2,847+', label: 'Active Patients' },
{ icon: <Award size={24} />, value: '94.2%', label: 'Recovery Rate' },
{ icon: <Heart size={24} />, value: '120+', label: 'Licensed Therapists' },
{ icon: <TrendingUp size={24} />, value: '18', label: 'Clinic Locations' }];


export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <Image
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1a5f269c1-1772659135376.png"
              alt="Physical therapist helping patient with rehabilitation exercises in a modern clinic"
              fill
              className="object-cover object-center"
              priority
              sizes="100vw" />
            
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,42,67,0.92) 0%, rgba(10,42,67,0.75) 60%, rgba(27,175,191,0.4) 100%)' }} />
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-6"
              style={{ background: 'rgba(27,175,191,0.2)', color: 'var(--secondary)', border: '1px solid rgba(27,175,191,0.3)' }}>
                About ASHA Recovery Group
              </div>
              <h1 className="text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                Restoring Mobility.<br />
                <span style={{ color: 'var(--secondary)' }}>Rebuilding Lives.</span>
              </h1>
              <p className="text-xl text-white/70 leading-relaxed max-w-2xl">
                Since 2008, ASHA Recovery Group has been the Chicagoland region's most trusted network of physical therapy and rehabilitation clinics — built on evidence, driven by outcomes, and guided by genuine care for every patient we serve.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="py-10 bg-white border-b border-border">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, idx) =>
              <div key={`stat-${idx}`} className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--teal-light)', color: 'var(--teal-mid)' }}>
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-3xl font-extrabold text-primary tabular-nums">{stat.value}</p>
                    <p className="text-sm text-muted-foreground font-medium">{stat.label}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 lg:py-28 bg-white">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
                style={{ background: 'var(--teal-light)', color: 'var(--teal-mid)' }}>
                  Our Mission
                </div>
                <h2 className="text-4xl lg:text-5xl font-extrabold text-primary leading-tight mb-6">
                  Care That Goes Beyond the Clinic
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  ASHA was founded on a simple but powerful belief: every patient deserves a recovery plan built around their life — not a generic protocol built around a diagnosis code.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  We built our network to serve the full spectrum of recovery needs — from elite athletes returning to competition, to seniors regaining independence, to workers recovering from occupational injuries. Our therapists are not just clinicians; they are partners in your recovery journey.
                </p>
                <div className="space-y-3">
                  {[
                  'JCAHO Accredited across all clinic locations',
                  'Most major insurance plans accepted',
                  'Dedicated physician liaison for every referring practice',
                  'EMR-compatible progress reporting',
                  'Telehealth services available for eligible patients'].
                  map((item) =>
                  <div key={`mission-${item}`} className="flex items-center gap-3">
                      <CheckCircle size={16} style={{ color: 'var(--secondary)' }} className="flex-shrink-0" />
                      <span className="text-sm text-foreground font-medium">{item}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
                  <Image
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_1a6098ad9-1776693634043.png"
                    alt="ASHA Recovery Group therapist working with patient in modern rehabilitation clinic"
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 50vw" />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
                </div>
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-border max-w-xs">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: 'var(--teal-light)' }}>
                      <AshaIcon name="success-recovery" size={22} color="var(--teal-mid)" />
                    </div>
                    <div>
                      <p className="font-bold text-primary text-sm">94.2% Recovery Rate</p>
                      <p className="text-xs text-muted-foreground">Above national average</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Clinical Philosophy — Three Pillars */}
        <section className="py-20 lg:py-28 overflow-hidden" style={{ background: 'var(--primary)' }}>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="max-w-2xl mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
              style={{ background: 'rgba(27,175,191,0.2)', color: 'var(--secondary)' }}>
                Our Clinical Philosophy
              </div>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight">
                Three Pillars of Every Recovery
              </h2>
              <p className="mt-4 text-white/60 text-lg leading-relaxed">
                These principles guide every clinical decision made across our network — from the first evaluation to discharge.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {pillars.map((pillar, idx) =>
              <div key={pillar.id}
              className="relative rounded-2xl p-8 transition-all duration-200 hover:-translate-y-1"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="absolute top-6 right-7 text-6xl font-black opacity-5 text-white tabular-nums select-none">
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-all duration-200 group-hover:scale-105"
                style={{ background: 'rgba(27,175,191,0.2)' }}>
                    <AshaIcon name={pillar.icon} size={28} color="#1BAFBF" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--secondary)' }}>
                    {pillar.subtitle}
                  </p>
                  <h3 className="text-xl font-bold text-white mb-4">{pillar.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed mb-8">{pillar.description}</p>
                  <div className="pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                    <p className="text-3xl font-extrabold text-white tabular-nums">{pillar.stat}</p>
                    <p className="text-sm text-white/50 mt-1">{pillar.statLabel}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Leadership Team */}
        <section className="py-20 lg:py-28 bg-white">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="max-w-2xl mx-auto text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
              style={{ background: 'var(--teal-light)', color: 'var(--teal-mid)' }}>
                Leadership Team
              </div>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-primary leading-tight">
                The People Behind the Care
              </h2>
              <p className="mt-4 text-muted-foreground text-lg">
                Our leadership team combines decades of clinical expertise with a shared commitment to patient-centered rehabilitation.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {teamMembers.map((member) =>
              <div key={member.id}
              className="bg-white border border-border rounded-2xl p-6 card-shadow hover:card-shadow-md transition-all duration-200 hover:-translate-y-0.5">
                  <div className="mb-5">
                    {member.photo ?
                  <div className="relative w-20 h-20 rounded-2xl overflow-hidden border-2 border-secondary/30">
                        <Image
                      src={member.photo}
                      alt={`${member.name}, ${member.title}`}
                      fill
                      className="object-cover object-top"
                      sizes="80px" />
                    
                      </div> :

                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-xl font-bold"
                  style={{ background: 'var(--primary)' }}>
                        {member.initials}
                      </div>
                  }
                  </div>
                  <h3 className="font-bold text-primary text-base leading-snug mb-1">{member.name}</h3>
                  <p className="text-xs font-semibold mb-3" style={{ color: 'var(--secondary)' }}>{member.title}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{member.bio}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* History Timeline */}
        <section className="py-20 lg:py-28" style={{ background: 'var(--background)' }}>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="max-w-2xl mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
              style={{ background: 'var(--teal-light)', color: 'var(--teal-mid)' }}>
                Our History
              </div>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-primary leading-tight">
                16 Years of Growth
              </h2>
              <p className="mt-4 text-muted-foreground text-lg">
                From a single clinic in the Chicago Loop to an 18-location network — built one patient outcome at a time.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
              {milestones.map((m, idx) =>
              <div key={`milestone-${idx}`}
              className="relative bg-white rounded-2xl p-6 border border-border card-shadow">
                  <div className="text-4xl font-extrabold mb-3 tabular-nums"
                style={{ color: 'var(--secondary)' }}>
                    {m.year}
                  </div>
                  <h3 className="font-bold text-primary mb-2">{m.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.description}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-24 bg-white">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="rounded-3xl p-10 lg:p-16 text-center" style={{ background: 'var(--primary)' }}>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-5">
                Ready to Start Your Recovery?
              </h2>
              <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
                Find a clinic near you or speak with our team to learn how ASHA can help you restore mobility and rebuild your life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/locations" className="btn-secondary flex items-center gap-2 px-6 py-3.5 rounded-2xl text-base">
                  <AshaIcon name="find-clinic" size={18} color="white" />
                  Find a Clinic
                </Link>
                <Link href="/contact" className="flex items-center gap-2 px-6 py-3.5 rounded-2xl border border-white/30 text-white hover:bg-white/10 transition-colors font-semibold text-base">
                  Contact Us
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
      <BackToTopButton />
    </div>);

}